import type {
  StorefrontTagMoveRequestType,
  StorefrontTagMoveResponseType,
} from "@sellar-io/api-v2/types";
import { Button, InputField, InputText } from "@sellar-io/components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type ReactElement } from "react";
import { useNavigate } from "react-router";
import { Alert } from "../../../../../components/alert/Alert";
import {
  Dialog,
  DialogContent,
  DialogCTA,
  DialogNavigation,
} from "../../../../../components/modal/Dialog";
import { Toast } from "../../../../../components/toast/Toast";
import { useToast } from "../../../../../components/toast/useToast.shared";
import { LAYOUT_GROUPING } from "../../../../../constants/routes";
import { ServerStateKeys } from "../../../../../hooks/api/v2/ServerStateKeys";
import {
  apiV2AssertError,
  apiV2AssertResponse,
  apiV2Request,
} from "../../../../../hooks/api/v2/apiV2Request";
import { useStorefrontTagCreate } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagCreate";
import { useStorefrontTagList } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useStorefrontTagUpdate } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagUpdate";
import { useFirebaseUser } from "../../../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { useSellarSupplier } from "../../../../../hooks/useSellarSupplier";
import { ProductSelection, type SelectableProduct } from "./ProductSelection";

export type BulkAssignableProduct = SelectableProduct;

export const BulkProductAssignmentDialog = ({
  active,
  onDismiss,
  products,
  currentGroupId,
  currentGroupName,
  totalGroupProducts,
  fixedDestination,
  moveAllProducts = false,
  initialSelectedIds = [],
  submitLabel,
  title = "Add products to a group",
}: {
  active: boolean;
  onDismiss: () => void;
  products: BulkAssignableProduct[];
  currentGroupId?: number;
  currentGroupName?: string;
  totalGroupProducts?: number;
  fixedDestination?: { id: number; name: string };
  moveAllProducts?: boolean;
  initialSelectedIds?: number[];
  submitLabel?: string;
  title?: string;
}): ReactElement => {
  const supplier = useSellarSupplier();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const id = useId();
  const [selectedIds, setSelectedIds] = useState<number[]>(initialSelectedIds);
  const [destinationId, setDestinationId] = useState<
    number | "new" | "ungrouped" | ""
  >(moveAllProducts ? "ungrouped" : "");
  const [newGroupName, setNewGroupName] = useState("");
  const [message, setMessage] = useState<string>();
  const [finishing, setFinishing] = useState(false);
  const tagsQuery = useStorefrontTagList(
    { query: [{ type: "SupplierId", value: supplier.id, modifier: "EQUALS" }] },
    { enabled: active && !fixedDestination },
  );
  const tags = (tagsQuery.data?.pages.flat() ?? []).filter(
    (tag) => tag.id !== currentGroupId,
  );
  const updateMutation = useStorefrontTagUpdate(
    fixedDestination?.id ??
      (typeof destinationId === "number" ? destinationId : 0),
  );
  const createMutation = useStorefrontTagCreate();
  const moveMutation = useMutation({
    mutationFn: (data: StorefrontTagMoveRequestType) =>
      apiV2Request({
        user,
        business,
        method: "post",
        path: `storefront-tags/${currentGroupId}/move-products`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse<StorefrontTagMoveResponseType>(response).data,
      ),
  });
  const isPending =
    finishing ||
    updateMutation.isPending ||
    createMutation.isPending ||
    moveMutation.isPending;
  const mutationError =
    moveMutation.error ?? updateMutation.error ?? createMutation.error;
  const willDelete =
    !fixedDestination &&
    !!currentGroupId &&
    (moveAllProducts ||
      (totalGroupProducts !== undefined &&
        totalGroupProducts > 0 &&
        selectedIds.length === totalGroupProducts));
  const dismiss = () => {
    if (!isPending) onDismiss();
  };
  const handleProductAssignmentSuccess = async ({
    groupName,
    count,
    sourceDeleted = false,
  }: {
    groupName: string | null;
    count: number;
    sourceDeleted?: boolean;
  }) => {
    setFinishing(true);
    // Navigate before invalidation can remove the source group and unmount its dialogs.
    if (sourceDeleted) navigate(LAYOUT_GROUPING, { replace: true });
    await Promise.all([
      ...[
        ServerStateKeys.StorefrontTags,
        ServerStateKeys.Products,
        ServerStateKeys.UngroupedProducts,
      ].map((key) =>
        queryClient.invalidateQueries({
          queryKey: [user?.uid, business?.id, key],
        }),
      ),
      queryClient.invalidateQueries({
        queryKey: [business?.id, ServerStateKeys.Products],
      }),
    ]);
    toast({
      placement: "bottom",
      render: () => (
        <Toast icon="CHECK_CIRCLE">
          {`${count} ${count === 1 ? "product" : "products"} ${groupName ? `${fixedDestination ? "added" : "moved"} to ${groupName}` : "left ungrouped"}${sourceDeleted ? ". Group deleted." : ""}`}
        </Toast>
      ),
    });
    onDismiss();
    setFinishing(false);
  };
  const handleMove = async () => {
    const destination: StorefrontTagMoveRequestType["destination"] =
      destinationId === "new"
        ? { type: "new", name: newGroupName.trim() }
        : destinationId === "ungrouped"
          ? { type: "ungrouped" }
          : { type: "group", id: Number(destinationId) };
    const result = await moveMutation.mutateAsync({
      ProductIds: selectedIds,
      destination,
      deleteSource: moveAllProducts,
    });
    await handleProductAssignmentSuccess({
      groupName: result.destinationName,
      count: result.movedCount,
      sourceDeleted: result.sourceDeleted,
    });
  };

  const handleCreate = async () => {
    const result = await createMutation.mutateAsync({
      value: newGroupName.trim(),
      ProductIds: selectedIds,
    });
    await handleProductAssignmentSuccess({
      groupName: result.value,
      count: selectedIds.length,
    });
  };

  const handleUpdate = async () => {
    const groupName =
      fixedDestination?.name ??
      tags.find((tag) => tag.id === destinationId)?.value;
    if (!groupName) return setMessage("Choose a valid destination group.");
    await updateMutation.mutateAsync({ ProductIds: selectedIds });
    await handleProductAssignmentSuccess({
      groupName,
      count: selectedIds.length,
    });
  };

  const submit = async () => {
    if (isPending) return;
    if (!moveAllProducts && selectedIds.length === 0)
      return setMessage("Select at least one product.");
    if (!fixedDestination && destinationId === "")
      return setMessage("Choose a destination group.");
    if (destinationId === "new" && !newGroupName.trim())
      return setMessage("Enter a name for the new group.");
    setMessage(undefined);
    try {
      if (currentGroupId && !fixedDestination) await handleMove();
      else if (destinationId === "new" && !fixedDestination)
        await handleCreate();
      else await handleUpdate();
    } catch {
      /* Mutation error is displayed below; preserve the selection for retry. */
    }
  };
  return (
    <Dialog active={active} onDismiss={dismiss}>
      <DialogNavigation
        title={title}
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={dismiss}
        description={
          fixedDestination
            ? `Select which of your ungrouped products you want to include in ${fixedDestination.name}`
            : moveAllProducts
              ? "Products, including inactive products, will be left ungrouped. You can optionally move them to another group."
              : currentGroupId
                ? `Select which products from your range you want to move from ${currentGroupName ?? "this group"}.`
                : "Choose which groups your ungrouped products should be shown in."
        }
      />
      <DialogContent className="flex flex-col gap-4 p-4">
        {mutationError && (
          <Alert
            type="error"
            message={
              apiV2AssertError(mutationError)?.response?.data.message ??
              "The products could not be moved. Try again."
            }
          />
        )}
        {message && <Alert type="warning" message={message} />}
        {!fixedDestination && tagsQuery.isError && (
          <Alert type="error" message="Groups could not be loaded.">
            <Button onClick={() => tagsQuery.refetch()}>Try again</Button>
          </Alert>
        )}
        {!moveAllProducts && (
          <ProductSelection
            products={products}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            disabled={isPending}
          />
        )}
        {fixedDestination ? (
          <InputField label="Adding to group">
            <InputText value={fixedDestination.name} disabled />
          </InputField>
        ) : (
          <div className="flex flex-col gap-2">
            <label className="font-medium" htmlFor={`${id}-destination`}>
              {moveAllProducts
                ? "Move products to another group (optional)"
                : "Choose a group"}
            </label>
            <select
              id={`${id}-destination`}
              className="h-10 rounded border border-zinc-200 bg-white px-3"
              value={destinationId}
              disabled={tagsQuery.isLoading || tagsQuery.isError || isPending}
              onChange={(event) => {
                const value = event.target.value;
                setDestinationId(
                  value === "new" || value === "ungrouped" || value === ""
                    ? value
                    : Number(value),
                );
              }}
            >
              {!moveAllProducts && (
                <option value="">
                  {tagsQuery.isLoading ? "Loading groups…" : "Select..."}
                </option>
              )}
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.value}
                </option>
              ))}
              <option value="new">Create a new group</option>
              {moveAllProducts && (
                <option value="ungrouped">Leave products ungrouped</option>
              )}
            </select>
            {tagsQuery.hasNextPage && (
              <button
                type="button"
                disabled={tagsQuery.isFetchingNextPage || isPending}
                onClick={() => tagsQuery.fetchNextPage()}
              >
                Load more groups
              </button>
            )}
            {destinationId === "new" && (
              <InputField label="New group name">
                <InputText
                  disabled={isPending}
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                  placeholder="Type a name..."
                />
              </InputField>
            )}
          </div>
        )}
        {willDelete && (
          <Alert
            type="warning"
            message={
              moveAllProducts
                ? "This group will be deleted after its products are moved or left ungrouped."
                : "Moving all remaining products will delete this empty group and return you to Layout & Grouping."
            }
          />
        )}
      </DialogContent>
      <DialogCTA>
        <Button
          loading={isPending}
          disabled={
            isPending ||
            (!fixedDestination &&
              destinationId !== "ungrouped" &&
              (tagsQuery.isLoading || tagsQuery.isError)) ||
            (!moveAllProducts && selectedIds.length === 0)
          }
          onClick={submit}
        >
          {moveAllProducts
            ? destinationId === "ungrouped"
              ? "Delete group"
              : "Move products and delete group"
            : (submitLabel ??
              (fixedDestination ? "Add products" : "Move products"))}
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
